import React, { createContext, useState, useRef, useEffect, useContext, useCallback, useMemo } from "react";

// Separate contexts to prevent unnecessary re-renders
const TimerTimeContext = createContext();
const TimerControlContext = createContext();

let start_time;

export const TimerProvider = ({ children }) => {
	const [time, setTime] = useState(0);
	const [isRunning, setIsRunning] = useState(false);
	const intervalRef = useRef(null);

	const Timer_start = useCallback(() => {
		if (!intervalRef.current) {
			setIsRunning(true);
			start_time = Date.now();
			intervalRef.current = setInterval(() => {
				setTime(() => Date.now() - start_time);
			}, 1000);
		}
	}, []);

	const Timer_stop = useCallback(() => {
		clearInterval(intervalRef.current);
		intervalRef.current = null;
		setIsRunning(false);
	}, []);

	const Timer_now = useCallback(() => {
		return Date.now() - start_time;
	}, []);

	const Timer_running = useCallback(() => {
		return isRunning;
	}, [isRunning]);

	useEffect(() => {
		return () => clearInterval(intervalRef.current);
	}, []);

	// Memoize control functions to prevent re-renders
	const controlValue = useMemo(() => ({
		Timer_start,
		Timer_stop,
		Timer_now,
		Timer_running
	}), [Timer_start, Timer_stop, Timer_now, Timer_running]);

	return (
		<TimerControlContext.Provider value={controlValue}>
			<TimerTimeContext.Provider value={time}>
				{children}
			</TimerTimeContext.Provider>
		</TimerControlContext.Provider>
	);
};

// Hook for components that only need timer controls (won't re-render on time updates)
export const useTimerControls = () => useContext(TimerControlContext);

// Hook for components that need the time value (will re-render every second)
export const useTimerTime = () => useContext(TimerTimeContext);

// Legacy hook - use useTimerControls instead to avoid re-renders
export const useTimer = () => {
	const controls = useContext(TimerControlContext);
	const time = useContext(TimerTimeContext);
	return { time, ...controls };
};
